<template>
  <div
    class="card"
    v-show="isSection('output')"
    v-if="getMode != 'production1'"
  >
    <div class="overflow-scroll-y">
      <div class="card-content myprofile-output">
        <div class="myprofile-output-hero">
          <p class="myprofile-output-confetti">&#127881;</p>
          <h2 class="myprofile-output-title">マイプロフィール完成！</h2>
          <p class="myprofile-output-lead">
            画像を保存して、友だちにシェアしよう
          </p>
        </div>

        <div class="myprofile-output-buttons">
          <el-button
            id="myprofile-output-save-btn"
            class="myprofile-save-btn"
            :loading="savingImage"
            @click="saveMyprofileImage"
          >
            <i class="el-icon-download" v-if="!savingImage"></i
            >&nbsp;画像を端末に保存する
          </el-button>
          <el-button
            id="myprofile-output-share-btn"
            v-if="canShareImage"
            class="myprofile-share-btn"
            :loading="sharingImage"
            @click="shareMyprofileImage"
          >
            <i class="el-icon-share" v-if="!sharingImage"></i
            >&nbsp;画像をシェアする
          </el-button>
        </div>
        <p class="myprofile-output-error" v-if="imageError">
          {{ imageError }}
        </p>

        <div class="myprofile-yf-cta">
          <span class="myprofile-yf-cta-badge">ヤギフレンズ限定</span>
          <p class="myprofile-yf-cta-title">
            プロフィールにはまだ空欄があります
          </p>
          <p class="myprofile-yf-cta-text">
            旧姓・出身地・出身小学校・ヤギディ診断結果は、ヤギフレンズに参加すると入力できます。全部うめて、あなたのマイプロフィールを完成させましょう！
          </p>
          <a
            href="/yagifriends"
            id="myprofile-output-yf-link"
            class="myprofile-yf-cta-btn"
          >
            ヤギフレンズで全部入力する <i class="el-icon-arrow-right"></i>
          </a>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useMyprofileOutput } from '@/composables/format-edit/useMyprofileOutput'
import { isCardSectionActive } from '@/utils/format-edit/cardSections'

const props = defineProps<{
  currentCardId?: string
  formatId?: string
}>()

const getMode = computed(() => process.env.VUE_APP_MODE)

const isSection = (section: string) =>
  isCardSectionActive(props.formatId, props.currentCardId, section)

const {
  savingImage,
  sharingImage,
  imageError,
  canShareImage,
  saveMyprofileImage,
  shareMyprofileImage
} = useMyprofileOutput()
</script>

<style lang="scss" scoped>
.myprofile-output {
  text-align: center;
  padding: 8px 16px 28px;

  .myprofile-output-hero {
    padding: 24px 0 4px;

    .myprofile-output-confetti {
      font-size: 42px;
      line-height: 1;
      margin-bottom: 8px;
    }

    .myprofile-output-title {
      font-size: 22px;
      font-weight: bold;
      color: #00342b;
      margin-bottom: 6px;
    }

    .myprofile-output-lead {
      font-size: 14px;
      color: #5a6e6a;
    }
  }

  .myprofile-output-buttons {
    display: flex;
    flex-direction: column;
    gap: 14px;
    align-items: center;
    margin: 22px 0 4px;

    .el-button {
      width: 100%;
      max-width: 340px;
      margin: 0;
      border: none;
      border-radius: 50px;
      padding: 16px 24px;
      font-size: 16px;
      font-weight: bold;
      transition:
        transform 0.15s ease,
        box-shadow 0.15s ease;

      &:hover {
        transform: translateY(-2px);
      }

      &:active {
        transform: translateY(0);
      }
    }

    .myprofile-save-btn {
      background: linear-gradient(135deg, #00c48c, #00a378);
      color: #fff;
      box-shadow: 0 6px 16px rgb(0 196 140 / 35%);

      &:hover,
      &:focus {
        background: linear-gradient(135deg, #0bd49a, #00b184);
        color: #fff;
        box-shadow: 0 8px 20px rgb(0 196 140 / 45%);
      }
    }

    .myprofile-share-btn {
      background: #fff;
      color: #00a378;
      border: 2px solid #00c48c;
      box-shadow: 0 4px 12px rgb(0 196 140 / 15%);

      &:hover,
      &:focus {
        background: #f0fbf8;
        color: #00a378;
      }
    }
  }

  .myprofile-output-error {
    color: #f56c6c;
    font-size: 13px;
    margin: 12px 0 0;
  }
}

.myprofile-yf-cta {
  position: relative;
  margin: 40px auto 8px;
  max-width: 420px;
  background: linear-gradient(135deg, #fff0f5 0%, #f4efff 55%, #eafaf4 100%);
  border-radius: 16px;
  padding: 28px 20px 20px;
  text-align: center;
  box-shadow: 0 4px 14px rgb(120 90 200 / 12%);

  .myprofile-yf-cta-badge {
    position: absolute;
    top: -12px;
    left: 50%;
    transform: translateX(-50%);
    background: #ff7aa2;
    color: #fff;
    font-size: 11px;
    font-weight: bold;
    border-radius: 50px;
    padding: 4px 14px;
    letter-spacing: 0.05em;
    white-space: nowrap;
  }

  .myprofile-yf-cta-title {
    font-weight: bold;
    font-size: 15px;
    color: #463a66;
    margin-bottom: 8px;
  }

  .myprofile-yf-cta-text {
    font-size: 13px;
    line-height: 1.8;
    color: #5d5475;
    text-align: left;
  }

  .myprofile-yf-cta-btn {
    display: block;
    margin-top: 16px;
    background: linear-gradient(135deg, #8f6fe8, #ff7aa2);
    color: #fff;
    font-weight: bold;
    font-size: 15px;
    text-align: center;
    border-radius: 50px;
    padding: 13px 16px;
    box-shadow: 0 6px 14px rgb(143 111 232 / 35%);
    transition:
      transform 0.15s ease,
      box-shadow 0.15s ease;

    i {
      margin-left: 4px;
    }

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 18px rgb(143 111 232 / 45%);
      color: #fff;
    }
  }
}
</style>
